import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '../api/client.js';

/**
 * Shared approve / reject mutations for a post (ADR-0029 P1).
 *
 * One source of truth for the approval state transitions + cache invalidation,
 * used by all three approval surfaces — the post-list pill (PostActions), the
 * Inbox card (with reject reason), and the PostDetail header. Each caller keeps
 * its own presentation; only the mutation + invalidation logic is shared.
 *
 * Options:
 *   onApprove()        — called after a successful approve
 *   onReject()         — called after a successful reject
 *   onPublish()        — called after a successful publish-now
 *   onRegenerate()     — called after a successful regenerate (cancelled → fresh)
 *   onError(err, kind) — kind is 'approve' | 'reject' | 'publish' | 'regenerate'
 *
 * `reject.mutate(reason)` accepts an optional rejection reason string.
 *
 * Action → endpoint map (all share the same invalidation):
 *   approve        → POST /posts/:id/approve
 *   reject         → POST /posts/:id/reject
 *   publishNow     → POST /posts/:id/publish-now   (approved → publish; also
 *                    re-attempts a failed publish — idempotent, never double-posts)
 *   regenerateFresh→ POST /posts/:id/retry         (cancelled → fresh AI draft)
 */
export function usePostApproval(postId, opts = {}) {
  const qc = useQueryClient();

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['posts'] });
    qc.invalidateQueries({ queryKey: ['post', postId] });
    qc.invalidateQueries({ queryKey: ['inbox'] });
    qc.invalidateQueries({ queryKey: ['loop-status'] });
  };

  const approve = useMutation({
    mutationFn: () => apiPost(`/posts/${postId}/approve`),
    onSuccess: () => { invalidate(); opts.onApprove?.(); },
    onError: (err) => opts.onError?.(err, 'approve'),
  });

  const reject = useMutation({
    mutationFn: (reason) => apiPost(`/posts/${postId}/reject`, reason ? { reason } : {}),
    onSuccess: () => { invalidate(); opts.onReject?.(); },
    onError: (err) => opts.onError?.(err, 'reject'),
  });

  const publishNow = useMutation({
    mutationFn: () => apiPost(`/posts/${postId}/publish-now`),
    onSuccess: () => { invalidate(); opts.onPublish?.(); },
    onError: (err) => opts.onError?.(err, 'publish'),
  });

  const regenerateFresh = useMutation({
    mutationFn: () => apiPost(`/posts/${postId}/retry`),
    onSuccess: () => { invalidate(); opts.onRegenerate?.(); },
    onError: (err) => opts.onError?.(err, 'regenerate'),
  });

  return { approve, reject, publishNow, regenerateFresh };
}
